import { useCallback, useEffect, useState } from "react";
import { PenLine } from "lucide-react";
import { theme, card, btnPrimary, inputStyle, type as text } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";
import { PortalLink } from "./PortalLink";
import { api, apiErrorMessage } from "../api/client";
import { runAiJob } from "../lib/aiJob";
import { runDocumentJob } from "../lib/generatePolicy";
import { useCurrentUser } from "../auth/useCurrentUser";
import { secondaryBtn } from "../pages/resilience/shared";

// Policy amendment drafts after a law change (docs/STANDARDS-WATCH.md "Amendments drafted from a notice", backend
// lib/policyAmendments.ts). For each document a law refresh notice lists, the AI can draft the amendment: the sections
// the change touches and their proposed wording. The owner edits each and applies them, which saves a new draft version
// of the document (no model call) to review and adopt as usual. Nothing is adopted or regenerated by a draft.

interface Section { heading: string; proposed: string; reason: string }
interface Draft { id: string; status: string; baseVersion: number; sections: Section[]; dropped: number; error: string | null; appliedVersion: number | null; stale: boolean }
interface DocRow { templateId: string; title: string; versionNumber: number; latestVersion: number | null; draft: Draft | null }
interface View { notice: { id: string; withdrawn: boolean }; documents: DocRow[] }

const TONE: Record<string, StatusTone> = { done: "partial", applied: "met", dismissed: "none", error: "gap", queued: "none", running: "none" };
const LABEL: Record<string, string> = { done: "Drafted, waiting for you", applied: "Applied", dismissed: "Dismissed", error: "Not drafted", queued: "Drafting…", running: "Drafting…" };

export function PolicyAmendmentDrafts({ noticeId }: { noticeId: string }) {
  const { user } = useCurrentUser();
  const canWrite = !!user?.permissions?.includes("documents:write");
  const [view, setView] = useState<View | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setView(await api.get<View>(`/policy-amendments/notices/${noticeId}`)); } catch (e) { setError(apiErrorMessage(e, "Could not load the amendment drafts.")); }
  }, [noticeId]);
  useEffect(() => { void load(); }, [load]);

  async function draft(templateId: string) {
    setBusy(templateId); setError(null);
    try { await runAiJob("/policy-amendments", (id) => `/policy-amendments/jobs/${id}`, { noticeId, templateId }); }
    catch (e) { setError(apiErrorMessage(e, "Could not draft the amendment.")); }
    finally { setBusy(null); await load(); }
  }

  if (!view || !canWrite || view.notice.withdrawn || view.documents.length === 0) return null;
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
      {view.documents.map((d) => (
        <div key={d.templateId} style={{ ...card }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ ...text.body, fontWeight: 600 }}>{d.title} <span style={text.meta}>version {d.latestVersion ?? d.versionNumber}</span></div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {d.draft && <StatusTag tone={TONE[d.draft.status] ?? "none"}>{d.draft.stale ? "Drafted for an older version" : LABEL[d.draft.status] ?? d.draft.status}</StatusTag>}
              {(!d.draft || ["error", "dismissed"].includes(d.draft.status) || d.draft.stale || d.draft.status === "applied") && (
                <button type="button" disabled={busy === d.templateId} style={{ ...btnPrimary, padding: "3px 10px", fontSize: 12, display: "inline-flex", gap: 4, alignItems: "center" }} onClick={() => void draft(d.templateId)}>
                  <PenLine size={12} /> {busy === d.templateId ? "Drafting…" : d.draft ? "Draft again" : "Draft the amendment"}
                </button>
              )}
            </div>
          </div>
          {d.draft?.status === "error" && d.draft.error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{d.draft.error}</div>}
          {d.draft?.status === "applied" && <div style={text.hint}>Saved as version {d.draft.appliedVersion}, a draft. Review and adopt it on <PortalLink to="/templates" style={{ color: theme.color.accentText }}>Policy templates</PortalLink>.</div>}
          {d.draft?.status === "done" && !d.draft.stale && <DraftEditor draft={d.draft} onDone={load} />}
        </div>
      ))}
    </div>
  );
}

function DraftEditor({ draft, onDone }: { draft: Draft; onDone: () => Promise<void> }) {
  const [texts, setTexts] = useState<Record<string, string>>(() => Object.fromEntries(draft.sections.map((s) => [s.heading, s.proposed])));
  const [picked, setPicked] = useState<Set<string>>(() => new Set(draft.sections.map((s) => s.heading)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function apply() {
    setBusy(true); setError(null);
    try {
      await runDocumentJob(`/policy-amendments/${draft.id}/apply`, { sections: draft.sections.filter((s) => picked.has(s.heading)).map((s) => ({ heading: s.heading, markdown: texts[s.heading] })) });
      await onDone();
    } catch (e) { setError(apiErrorMessage(e, "Could not apply the amendment.")); }
    finally { setBusy(false); }
  }
  async function dismiss() {
    setBusy(true); setError(null);
    try { await api.post(`/policy-amendments/${draft.id}/dismiss`, {}); await onDone(); }
    catch (e) { setError(apiErrorMessage(e, "Could not dismiss the draft.")); }
    finally { setBusy(false); }
  }

  if (draft.sections.length === 0) {
    return (
      <div style={{ ...text.hint, marginTop: 6 }}>
        The AI found nothing in this document that the change makes wrong. <button type="button" style={secondaryBtn} onClick={() => void dismiss()}>Dismiss</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 8, display: "grid", gap: 10 }}>
      <div style={text.hint}>Edit each section as it should read. Applying saves a new draft version with only these sections changed; nothing is adopted until you adopt it.</div>
      {draft.sections.map((s) => (
        <div key={s.heading}>
          <label style={{ ...text.label, display: "flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={picked.has(s.heading)} onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(s.heading); else n.delete(s.heading); return n; })} />
            {s.heading}
          </label>
          {s.reason && <div style={text.meta}>{s.reason}</div>}
          <textarea aria-label={`Wording for ${s.heading}`} value={texts[s.heading]} onChange={(e) => setTexts((t) => ({ ...t, [s.heading]: e.target.value }))} rows={Math.min(14, Math.max(4, texts[s.heading].split("\n").length + 1))} style={{ ...inputStyle, width: "100%", fontFamily: "inherit", marginTop: 4 }} />
        </div>
      ))}
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" disabled={busy || picked.size === 0} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }} onClick={() => void apply()}>{busy ? "Saving…" : "Apply as a new draft version"}</button>
        <button type="button" disabled={busy} style={secondaryBtn} onClick={() => void dismiss()}>Dismiss</button>
      </div>
    </div>
  );
}
